import { XStack, YStack } from '@lyra/core/components'
import BodyText from '@lyra/core/components/BodyText'
import { CornerDownRight } from '@lyra/core/components/Icon'
import Table, { createColumnHelper, TableData, TableProps } from '@lyra/core/components/Table'
import formatNumber from '@lyra/core/utils/formatNumber'
import { InstrumentType } from '@lyra/web/constants/instruments'
import { MARKET_ICON_SIZE } from '@lyra/web/constants/layout'
import { MarketId } from '@lyra/web/constants/markets'
import { PageId } from '@lyra/web/constants/pages'
import { Position } from '@lyra/web/constants/position'
import { getInstrumentPagePath, parseInstrumentName } from '@lyra/web/utils/instruments'
import { formatMarketName } from '@lyra/web/utils/markets'
import { getPagePath } from '@lyra/web/utils/pages'
import { usePathname, useRouter } from 'next/navigation'
import React, { useMemo } from 'react'
import { Stack } from 'tamagui'

import MarketIcon from './MarketIcon'
import PositionCell from './PositionCell'

type GreeksTableData = TableData<{
  id: string
  marketId: MarketId
  title?: string
  subtitle?: string
  instrumentName?: string
  leverage?: number
  amount?: number
  isLong?: boolean
  delta: number
  vega: number
  gamma: number
  theta: number
}>

type Props = {
  positions: Position[]
} & Omit<TableProps<GreeksTableData>, 'columns' | 'data'>

const marketOrderList = [MarketId.ETH, MarketId.BTC]

const getMarketOrderIndex = (marketId: MarketId) => {
  const index = marketOrderList.indexOf(marketId)
  return index === -1 ? marketOrderList.length : index // If not found, put it at the end
}

const getPositionTypeOrder = (position: GreeksTableData) => {
  if (position.id.startsWith('total')) {
    return 1 // Total should come first
  } else if (position.instrumentName?.endsWith('PERP')) {
    return 2 // Perpetual contracts come second
  } else {
    return 3 // Everything else comes after
  }
}

const sortGreeksTable = (positions: GreeksTableData[]) => {
  return positions.sort((a, b) => {
    // Sort by market order first, according to the marketOrderList
    const marketIndexA = getMarketOrderIndex(a.marketId)
    const marketIndexB = getMarketOrderIndex(b.marketId)
    if (marketIndexA !== marketIndexB) {
      return marketIndexA - marketIndexB
    }

    // If marketIds are the same, then sort by position type order
    const orderA = getPositionTypeOrder(a)
    const orderB = getPositionTypeOrder(b)
    if (orderA !== orderB) {
      return orderA - orderB
    }

    const instrumentA = parseInstrumentName(a.instrumentName || '')
    const instrumentB = parseInstrumentName(b.instrumentName || '')

    const strikeA = instrumentA?.type === InstrumentType.Options ? instrumentA.strikePrice : 0
    const strikeB = instrumentB?.type === InstrumentType.Options ? instrumentB.strikePrice : 0
    if (strikeA !== strikeB) {
      return strikeA - strikeB
    }

    // If both are the same type within the same market, sort by instrumentName or any other property
    return a.instrumentName?.localeCompare(b.instrumentName || '') || 0
  })
}

const GreeksTable = ({ positions, ...tableProps }: Props): JSX.Element => {
  const router = useRouter()
  const pathname = usePathname()
  const isPerpsPage = pathname.startsWith('/perps')

  const rows: GreeksTableData[] = useMemo(() => {
    const marketGreeks: Record<string, GreeksTableData> = {}
    // Combine the totals and the individual positions into one array
    const combinedRows: GreeksTableData[] = []

    positions.forEach((position) => {
      const instrument = parseInstrumentName(position.instrument_name)
      if (instrument?.type === InstrumentType.Spot) {
        return
      }

      const marketId = instrument?.marketId

      if (!marketId) return

      const { amount, instrument_name, delta, vega, gamma, theta } = position
      const isLong = +amount > 0
      const positionAmount = Math.abs(parseFloat(amount))
      const instrumentPath = getInstrumentPagePath(instrument_name)
      // Calculate individual position greeks
      const individualGreeks: GreeksTableData = {
        id: instrument_name,
        marketId,
        isLong,
        instrumentName: instrument_name,
        delta: positionAmount * (isLong ? parseFloat(delta) : -1 * parseFloat(delta)),
        vega: positionAmount * (isLong ? parseFloat(vega) : -1 * parseFloat(vega)),
        gamma: positionAmount * (isLong ? parseFloat(gamma) : -1 * parseFloat(gamma)),
        theta: positionAmount * (isLong ? parseFloat(theta) : -1 * parseFloat(theta)),
        onPress: () => router.push(instrumentPath),
      }
      combinedRows.push(individualGreeks)
      // Update market totals
      if (!marketGreeks[marketId]) {
        marketGreeks[marketId] = {
          ...individualGreeks,
          id: `total-${marketId}`,
          instrumentName: undefined,
          onPress: () =>
            router.push(
              // default to options page, unless user is on perps page
              getPagePath(
                isPerpsPage ? { page: PageId.Perps, marketId } : { page: PageId.Options, marketId }
              )
            ),
        }
      } else {
        marketGreeks[marketId].delta += individualGreeks.delta
        marketGreeks[marketId].vega += individualGreeks.vega
        marketGreeks[marketId].gamma += individualGreeks.gamma
        marketGreeks[marketId].theta += individualGreeks.theta
      }
    })
    return sortGreeksTable(combinedRows.concat(Object.values(marketGreeks)))
  }, [isPerpsPage, positions, router])

  const columnHelper = createColumnHelper<GreeksTableData>()

  const columns = useMemo(
    () => [
      columnHelper.accessor('marketId', {
        id: 'time',
        header: 'Asset',
        meta: {
          minWidth: 230,
        },
        cell: (props) => {
          const { instrumentName, leverage, isLong, amount } = props.row.original
          return !instrumentName ? (
            <XStack alignItems="center" gap="$1">
              <MarketIcon size={MARKET_ICON_SIZE} marketId={props.row.original.marketId} />
              <YStack>
                <BodyText>{props.cell.getValue()}</BodyText>
                <BodyText color="secondary">
                  {formatMarketName(props.row.original.marketId)}
                </BodyText>
              </YStack>
            </XStack>
          ) : (
            <PositionCell
              icon={
                <Stack width={36} height={36} alignItems="center" justifyContent="center">
                  <CornerDownRight color="$invertedBg" size={16} strokeWidth={3} />
                </Stack>
              }
              instrumentName={instrumentName}
              leverage={leverage}
              isLong={!!isLong}
              size={amount}
            />
          )
        },
      }),
      columnHelper.accessor('delta', {
        id: 'delta',
        header: 'Delta',
        cell: (props) => (
          <BodyText>{formatNumber(props.cell.getValue(), { showSign: true, dps: 3 })}</BodyText>
        ),
      }),
      columnHelper.accessor('vega', {
        id: 'vega',
        header: 'Vega',
        cell: (props) => (
          <BodyText>{formatNumber(props.cell.getValue(), { showSign: true, dps: 3 })}</BodyText>
        ),
      }),
      columnHelper.accessor('gamma', {
        id: 'gamma',
        header: 'Gamma',
        cell: (props) => (
          <BodyText>{formatNumber(props.cell.getValue(), { showSign: true, dps: 3 })}</BodyText>
        ),
      }),
      columnHelper.accessor('theta', {
        id: 'theta',
        header: 'Theta',
        cell: (props) => (
          <BodyText>{formatNumber(props.cell.getValue(), { showSign: true, dps: 3 })}</BodyText>
        ),
      }),
    ],
    [columnHelper]
  )

  // Note: greeks should not have page size
  return (
    <Table
      emptyMessage="No open positions"
      {...tableProps}
      size="lg"
      data={rows}
      columns={columns}
    />
  )
}

export default GreeksTable
